
/**
 * Utility functions for formatting various data types
 */

/**
 * Returns a formatted label for power type based on charger type and power output
 * @param chargerType The type of charger
 * @param powerOutput The power output in kW
 * @returns A formatted power type label
 */
export const getPowerTypeLabel = (chargerType: string | undefined, powerOutput: number): string => {
  if (!chargerType) return 'Standard Charging';
  
  const chargerTypeLower = chargerType.toLowerCase();
  
  if (chargerTypeLower.includes('dc') || chargerTypeLower.includes('fast')) {
    return 'DC Fast Charging';
  } else if (powerOutput >= 22) {
    return 'Level 2 - Fast';
  } else if (powerOutput >= 7) {
    return 'Level 2 - Medium';
  } else if (powerOutput > 0) {
    return 'Level 1 - Slow';
  } else {
    return 'Standard Charging';
  }
};

/**
 * Returns a formatted price with 2 decimal places
 * @param price The price to format
 * @param fallback Optional fallback value (defaults to '0.00')
 * @returns A formatted price string
 */
export const formatPrice = (price: number | undefined | null, fallback: string = '0.00'): string => {
  if (typeof price !== 'number' || isNaN(price)) {
    return fallback;
  }
  return price.toFixed(2);
};

/**
 * Returns a formatted distance with appropriate units
 * @param distance The distance in kilometers
 * @param fallback Optional fallback value (defaults to '0 m')
 * @returns A formatted distance string
 */
export const formatDistance = (distance: number | undefined | null, fallback: string = '0 m'): string => {
  if (typeof distance !== 'number' || isNaN(distance)) {
    return fallback;
  }
  
  if (distance < 1) {
    return `${Math.round(distance * 1000)} m`;
  }
  return `${distance.toFixed(1)} km`;
};

/**
 * Returns a formatted duration in hours and minutes
 * @param hours The duration in hours
 * @returns A formatted duration string
 */
export const formatDuration = (hours: number | undefined | null): string => {
  if (typeof hours !== 'number' || isNaN(hours) || hours <= 0) {
    return '0h 0m';
  }
  
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  
  if (minutes === 60) {
    return `${wholeHours + 1}h 0m`;
  }
  
  return `${wholeHours}h ${minutes}m`;
};

/**
 * Returns a formatted date string
 * @param date The date to format
 * @returns A formatted date string
 */
export const formatDate = (date: Date | string | undefined | null): string => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    return 'Invalid date';
  }
};

/**
 * Returns a formatted time string
 * @param timeString The time string to format (e.g., "14:30")
 * @returns A formatted time string
 */
export const formatTime = (timeString: string | undefined | null): string => {
  if (!timeString || !/^\d{1,2}:\d{2}$/.test(timeString)) {
    return 'N/A';
  }
  
  try {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0);
    
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return 'Invalid time';
  }
};

/**
 * Returns a formatted energy value with kWh unit
 * @param value The energy value in kWh
 * @returns A formatted energy string
 */
export const formatEnergy = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0 kWh';
  }
  
  return `${value.toFixed(1)} kWh`;
};

/**
 * Formats a number with thousand separators
 * @param num The number to format
 * @returns A formatted number string
 */
export const formatNumber = (num: number | undefined | null): string => {
  if (typeof num !== 'number' || isNaN(num)) {
    return '0';
  }
  
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Returns a formatted percentage value
 * @param value The percentage value (0-100)
 * @returns A formatted percentage string
 */
export const formatPercentage = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0%';
  }
  
  return `${Math.round(value)}%`;
};
